import { Button, Select, Modal, Tooltip, Form, Input, Empty } from 'antd';
import { DeleteFilled, EditFilled } from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';
import {
  addUpdateUserSkillDomain,
  AddUpdateUserSkillDomainReqType,
  getAllDomains,
  GetAllDomains,
  getAllUserDomains,
  GetAllUserDomainsResType,
} from '../../services/skillMatrixService';
import Table, { ColumnsType } from 'antd/es/table';
import NotificationAlert from '../notification';

type Props = {
  isReadOnlyMode?: boolean;
  userId: string;
};

function DomainRating({ isReadOnlyMode, userId }: Props) {
  const [form] = Form.useForm();
  const myRef = useRef<any>();
  const [domains, setDomains] = useState<GetAllUserDomainsResType[]>([]);
  const [listOfDomains, setListOfDomains] = useState<GetAllDomains[]>([]);
  const [isLoading, setIsLoading] = useState({
    table: true,
    modal: false,
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] =
    useState<GetAllUserDomainsResType | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);

  const fetchDomainData = () => {
    // Reset loading states
    setIsLoading((prev) => ({ ...prev, table: true }));

    Promise.all([getAllDomains(), getAllUserDomains(userId)])
      .then(([domainsRes, userDomainsRes]) => {
        if (domainsRes.error) {
          myRef.current?.call('error', domainsRes.error?.message);
          setListOfDomains([]);
        } else {
          setListOfDomains(domainsRes.data || []);
        }

        if (userDomainsRes.error) {
          myRef.current?.call('error', userDomainsRes.error?.message);
          setDomains([]);
        } else {
          setDomains(userDomainsRes.data || []);
        }
      })
      .catch((error) => {
        myRef.current?.call('error', 'Failed to load domain data');
        console.error(error);
        setDomains([]);
        setListOfDomains([]);
      })
      .finally(() => {
        setIsLoading((prev) => ({ ...prev, table: false }));
      });
  };

  useEffect(() => {
    fetchDomainData();
  }, [userId]);

  const handleEditDomain = (record: GetAllUserDomainsResType) => {
    setIsEditMode(true);
    setCurrentRecord(record);
    setIsModalVisible(true);

    // Populate form with existing data
    form.setFieldsValue({
      domainId: record.domain.id,
      rating: record.rating,
    });
  };

  const handleDeleteDomain = (record: GetAllUserDomainsResType) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this domain?',
      onOk: () => {
        setIsLoading((prev) => ({ ...prev, table: true }));
        const updatedDomains = domains.filter(
          (domain) => domain.id !== record.id
        );

        addUpdateUserSkillDomain(
          updatedDomains.map((ud) => ({
            DomainId: ud.domainId,
            Rating: ud.rating,
            UserId: ud.userId,
          }))
        )
          .then(({ data, error }) => {
            if (error) {
              myRef.current?.call(
                'error',
                error.message || 'Failed to update domain'
              );
              return;
            }
            setDomains(data || []);
            myRef.current?.call('success', 'Domain deleted successfully');
          })
          .finally(() => {
            setIsLoading((prev) => ({ ...prev, table: false }));
          });
      },
    });
  };

  const handleAddNewDomain = () => {
    setIsEditMode(false);
    setCurrentRecord(null);
    setIsModalVisible(true);
    form.resetFields();
  };

  const handleModalOk = () => {
    form
      .validateFields()
      .then((values) => {
        setIsLoading((prev) => ({ ...prev, modal: true }));

        // Check if the domain already exists (excluding the current record when editing)
        const existingDomain = domains.find(
          (domain) =>
            domain.domainId === values.domainId &&
            (!isEditMode || (currentRecord && domain.id !== currentRecord.id))
        );

        if (existingDomain) {
          myRef.current?.call('error', 'This domain already exists.');
          setIsLoading((prev) => ({ ...prev, modal: false }));
          return;
        }

        // Prepare request data for adding/updating user skill domain
        const reqData: AddUpdateUserSkillDomainReqType[] = [
          ...(isEditMode && currentRecord
            ? domains
                .filter((d) => d.id !== currentRecord.id)
                .map((ud) => ({
                  DomainId: ud.domainId,
                  Rating: ud.rating,
                  UserId: ud.userId,
                }))
            : domains.map((ud) => ({
                DomainId: ud.domainId,
                Rating: ud.rating,
                UserId: ud.userId,
              }))),
          {
            UserId: userId,
            DomainId:
              isEditMode && currentRecord
                ? currentRecord.domain.id
                : values.domainId,
            Rating: values.rating,
          },
        ];

        // Call service to add/update user skill domain
        addUpdateUserSkillDomain(reqData)
          .then(({ data, error }) => {
            if (error) {
              myRef.current?.call(
                'error',
                error.message || 'Failed to update domain'
              );
              return;
            }

            setDomains(data || []);

            if (isEditMode && currentRecord) {
              myRef.current?.call('success', 'Domain updated successfully');
            } else {
              myRef.current?.call('success', 'Domain added successfully');
            }

            setIsModalVisible(false);
            form.resetFields();
          })
          .catch((error) => {
            myRef.current?.call(
              'error',
              'An error occurred while saving the domain'
            );
            console.error(error);
          })
          .finally(() => {
            setIsLoading((prev) => ({ ...prev, modal: false }));
          });
      })
      .catch((errorInfo) => {
        console.error('Validate Failed:', errorInfo);
      });
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const checkFormValidity = async () => {
    try {
      const values = form.getFieldsValue();
      const requiredFields = ['domainId', 'rating'];
      const allFieldsFilled = requiredFields.every((field) => {
        const value = values[field];
        return value !== undefined && value !== null && value !== '';
      });

      // rating must be between 1 and 5
      const rating = Number(values.rating);
      if (rating < 1 || rating > 5) {
        setIsFormValid(false);
        return;
      }

      // Check if the domain already exists in the list of domains if editing
      const domainExists = !currentRecord
        ? domains.some((domain) => domain.domainId === values.domainId)
        : false;

      if (currentRecord && allFieldsFilled) {
        // Check if current form values match the selected domain's values
        const matchesOriginal =
          values.domainId === currentRecord.domain.id &&
          values.rating === currentRecord.rating.toString();

        setIsFormValid(!matchesOriginal && !domainExists);
      } else {
        setIsFormValid(allFieldsFilled && !domainExists);
      }
    } catch (error) {
      setIsFormValid(false);
    }
  };

  const headers: ColumnsType<GetAllUserDomainsResType> = [
    {
      title: 'Domain Name',
      dataIndex: ['domain', 'name'],
      key: 'domainName',
      sorter: (a, b) => a.domain.name.localeCompare(b.domain.name),
    },
    {
      title: 'Rating',
      dataIndex: 'rating',
      key: 'rating',
    },
    {
      title: 'Actions',
      key: 'actions',
      hidden: isReadOnlyMode,
      render: (_, record) =>
        !isReadOnlyMode ? (
          <div className="flex gap-x-[10px]">
            <Tooltip title="Edit Skill">
              <Button
                icon={<EditFilled />}
                onClick={() => handleEditDomain(record)}
              />
            </Tooltip>

            <Tooltip title="Delete Skill">
              <Button
                icon={<DeleteFilled />}
                onClick={() => handleDeleteDomain(record)}
              />
            </Tooltip>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Domain Expertise Rating</h2>
        <div className="flex">
          {!isReadOnlyMode && (
            <Button
              type="primary"
              onClick={handleAddNewDomain}
              disabled={isLoading.table}
            >
              Add New Domain
            </Button>
          )}
        </div>
      </div>

      <Table
        columns={headers}
        dataSource={domains}
        loading={isLoading.table}
        locale={{
          emptyText: isLoading.table ? (
            'Loading...'
          ) : (
            <Empty description="No domains found" />
          ),
        }}
        rowKey="id"
      />

      <Modal
        title={isEditMode ? 'Edit Domain' : 'Add New Domain'}
        open={isModalVisible}
        footer={[
          <Button key="cancel" onClick={handleModalCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleModalOk}
            disabled={!isFormValid}
          >
            {isEditMode ? 'Confirm' : 'Create'}
          </Button>,
        ]}
        onCancel={handleModalCancel}
        confirmLoading={isLoading.modal}
      >
        <Form form={form} layout="vertical" onValuesChange={checkFormValidity}>
          <Form.Item
            name="domainId"
            label="Domain"
            rules={[
              { required: true, message: 'Please select a domain' },
              {
                validator: (_, value) => {
                  const domainExists = domains.some(
                    (domain) =>
                      domain.domainId === value &&
                      (!isEditMode ||
                        (currentRecord && domain.id !== currentRecord.id))
                  );

                  if (domainExists) {
                    setIsFormValid(false);
                    return Promise.reject(
                      'This domain already exists in your domain expertise table. Please select a new domain.'
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Select
              placeholder="Select a domain"
              disabled={isEditMode || isLoading.modal}
            >
              {listOfDomains.map((domain) => (
                <Select.Option key={domain.id} value={domain.id}>
                  {domain.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="rating"
            label="Rating"
            rules={[
              { required: true, message: 'Please input the rating' },
              {
                validator: (_, value) => {
                  const numValue = Number(value);
                  if (isNaN(numValue) || numValue < 1 || numValue > 5) {
                    return Promise.reject('Rating must be between 1 and 5');
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input type="number" min={1} max={5} disabled={isLoading.modal} />
          </Form.Item>
        </Form>
      </Modal>

      <NotificationAlert ref={myRef} />
    </>
  );
}

export default DomainRating;
